import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HelpCircle, Plus, Edit, Trash2, Loader2, X } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast, ToastManager } from '@/components/ui/Toast';

const QUESTION_MAX_LENGTH = 300;

interface Faq {
    faq_id: number;
    question: string;
    answer: string;
    display_order: number;
    created_at: string;
    updated_at: string;
}

const cut = (s: string | undefined | null, n: number) =>
    s && s.length > n ? s.substring(0, n) + '...' : s || '-';

// Slide-in panel used for both Add and Edit — mirrors the pattern used in admin/jobs/index.tsx
const SliderOverlay: React.FC<{
    open: boolean;
    onClose: () => void;
    title: string;
    icon: React.ReactNode;
    footer?: React.ReactNode;
    children: React.ReactNode;
}> = ({ open, onClose, title, icon, footer, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[9999]">
            <div onClick={onClose} className="fixed inset-0 bg-black/60" />
            <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 bg-brandColor">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">{icon}</div>
                        <h3 className="text-base font-semibold text-white">{title}</h3>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">{children}</div>
                {footer && <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-200 bg-gray-50">{footer}</div>}
            </div>
        </div>
    );
};

const FAQs: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editFaq, setEditFaq] = useState<Faq | null>(null);
    const [form, setForm] = useState({ question: '', answer: '' });
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    useEffect(() => {
        fetchFaqs();
    }, [id]);

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(`/schools/${id}/faqs`);
            if (response.success) {
                setFaqs(response.data?.faqs || []);
            } else {
                showToast(response.message || 'Failed to load FAQs', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to load FAQs', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openAdd = () => {
        setForm({ question: '', answer: '' });
        setEditFaq(null);
        setShowForm(true);
    };

    const openEdit = (faq: Faq) => {
        setForm({ question: faq.question, answer: faq.answer });
        setEditFaq(faq);
        setShowForm(true);
    };

    const submit = async () => {
        const question = form.question.trim();
        const answer = form.answer.trim();
        if (!question || !answer) {
            showToast('Question and answer are required', 'error');
            return;
        }
        if (question.length > QUESTION_MAX_LENGTH) {
            showToast(`Question must be ${QUESTION_MAX_LENGTH} characters or less`, 'error');
            return;
        }
        try {
            setSubmitting(true);
            const response = await apiRequest(
                editFaq ? `/schools/${id}/faqs/${editFaq.faq_id}` : `/schools/${id}/faqs`,
                { method: editFaq ? 'PUT' : 'POST', body: { question, answer } }
            );
            if (response.success) {
                showToast(editFaq ? 'FAQ updated!' : 'FAQ added!', 'success');
                setShowForm(false);
                await fetchFaqs();
            } else {
                showToast(response.message || 'Failed to save FAQ', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to save FAQ', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (faqId: number) => {
        if (!confirm('Delete this FAQ?')) return;
        try {
            setDeletingId(faqId);
            const response = await apiRequest(`/schools/${id}/faqs/${faqId}`, { method: 'DELETE' });
            if (response.success) {
                showToast('FAQ removed!', 'success');
                setFaqs(prev => prev.filter(f => f.faq_id !== faqId));
            } else {
                showToast(response.message || 'Failed to remove FAQ', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to remove FAQ', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-brandColor rounded-lg text-white">
                        <HelpCircle className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">FAQs</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{faqs.length} question{faqs.length === 1 ? '' : 's'}</p>
                    </div>
                </div>
                <button
                    onClick={openAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add FAQ
                </button>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
                </div>
            ) : (
                <div className="flex-1 overflow-auto p-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {faqs.length === 0 ? (
                            <div className="text-center py-14">
                                <HelpCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">No FAQs added yet</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click "Add FAQ" to create the first one</p>
                            </div>
                        ) : (
                            <table className="min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-12">#</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Question</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Answer</th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase w-24">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {faqs.map((faq, i) => (
                                        <tr key={faq.faq_id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                                            <td className="px-4 py-3 text-sm text-gray-500">{i + 1}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{cut(faq.question, 60)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{cut(faq.answer, 80)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => openEdit(faq)}
                                                        className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(faq.faq_id)}
                                                        disabled={deletingId === faq.faq_id}
                                                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                                    >
                                                        {deletingId === faq.faq_id
                                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                                            : <Trash2 className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            <SliderOverlay
                open={showForm}
                onClose={() => setShowForm(false)}
                title={editFaq ? 'Edit FAQ' : 'Add FAQ'}
                icon={<HelpCircle className="w-4 h-4 text-white" />}
                footer={
                    <>
                        <button
                            onClick={() => setShowForm(false)}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-lg hover:bg-brand-hover disabled:opacity-50"
                        >
                            {submitting
                                ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</>
                                : (editFaq ? 'Save Changes' : 'Add FAQ')}
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Question <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.question}
                            maxLength={QUESTION_MAX_LENGTH}
                            onChange={(e) => setForm(prev => ({ ...prev, question: e.target.value }))}
                            placeholder="e.g. What is the admission process?"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brandColor/20 focus:border-brandColor outline-none"
                        />
                        <p className="mt-1 text-xs text-gray-400">{form.question.length}/{QUESTION_MAX_LENGTH}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Answer <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={form.answer}
                            onChange={(e) => setForm(prev => ({ ...prev, answer: e.target.value }))}
                            rows={5}
                            placeholder="Answer..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-brandColor/20 focus:border-brandColor outline-none"
                        />
                    </div>
                </div>
            </SliderOverlay>

            <ToastManager />
        </div>
    );
};

export default FAQs;
